import { Application, Container, Graphics, Ticker } from "pixi.js";
import type { Lane, NoteEvent, Section, TrackData } from "../types";
import { Background } from "./background";
import { GRAY_COLOR } from "./palette";
import { GridState } from "./grid";
import { Hud } from "./hud";
import { TrackGeometry } from "./track";

const PLAYER_SCREEN_X = 0.3; // fraction of viewport width
const COLLECT_WINDOW = 0.14; // seconds of grace around a note
const NOTE_RADIUS = 12;

const enum NoteState {
  Pending,
  Collected,
  Missed,
}

export interface GameStats {
  score: number;
  collected: number;
  missed: number;
  combos: number;
  fullRows: number;
  totalNotes: number;
}

export class Game {
  private readonly geometry: TrackGeometry;
  private readonly grid = new GridState();
  private readonly bg = new Background();
  private readonly hud: Hud;
  private readonly world = new Container();
  private readonly trackG = new Graphics();
  private readonly notesLayer = new Container();
  private readonly player = new Container();

  private readonly noteStates: NoteState[];
  private readonly noteSprites = new Map<number, Graphics>();
  private readonly fx: { g: Graphics; life: number }[] = [];

  private source: AudioBufferSourceNode | null = null;
  private startAt = 0;
  private lane: Lane = 1;
  private playerY = 0;
  private camY = 0;
  private camYInit = false;
  private sectionIdx = 0;
  private noteScanFrom = 0;
  private ended = false;
  private lastW = 0;
  private lastH = 0;

  private stats: GameStats;

  constructor(
    private readonly app: Application,
    private readonly data: TrackData,
    private readonly audioCtx: AudioContext,
    private readonly audioBuffer: AudioBuffer,
    songName: string,
    private readonly onEnd: (stats: GameStats) => void,
  ) {
    this.geometry = new TrackGeometry(data);
    this.hud = new Hud(songName, data.intensity);
    this.noteStates = new Array<NoteState>(data.notes.length).fill(NoteState.Pending);
    // Accuracy is measured against collectible circles only — grays don't count.
    const colorNotes = data.notes.reduce((acc, n) => acc + (n.kind === "color" ? 1 : 0), 0);
    this.stats = { score: 0, collected: 0, missed: 0, combos: 0, fullRows: 0, totalNotes: colorNotes };

    this.buildPlayer();
    this.world.addChild(this.trackG, this.notesLayer, this.player);
    this.app.stage.addChild(this.bg.container, this.world, this.hud.container);

    this.hud.onLaneChange = (dir) => this.moveLane(dir);
    this.onKey = this.onKey.bind(this);
    window.addEventListener("keydown", this.onKey);

    this.resize();
    this.update = this.update.bind(this);
  }

  start(): void {
    const source = this.audioCtx.createBufferSource();
    source.buffer = this.audioBuffer;
    source.connect(this.audioCtx.destination);
    this.startAt = this.audioCtx.currentTime + 0.15;
    source.start(this.startAt);
    this.source = source;
    this.app.ticker.add(this.update);
  }

  destroy(): void {
    this.app.ticker.remove(this.update);
    window.removeEventListener("keydown", this.onKey);
    try {
      this.source?.stop();
    } catch {
      /* already stopped */
    }
    this.app.stage.removeChildren();
    this.bg.container.destroy({ children: true });
    this.world.destroy({ children: true });
    this.hud.container.destroy({ children: true });
  }

  private resize(): void {
    this.lastW = this.app.screen.width;
    this.lastH = this.app.screen.height;
    this.bg.resize(this.lastW, this.lastH);
    this.hud.resize(this.lastW, this.lastH);
  }

  private onKey(e: KeyboardEvent): void {
    if (e.key === "ArrowUp") this.moveLane(1);
    else if (e.key === "ArrowDown") this.moveLane(-1);
  }

  private moveLane(dir: 1 | -1): void {
    const next = Math.min(2, Math.max(0, this.lane + dir)) as Lane;
    this.lane = next;
  }

  private get songTime(): number {
    return this.audioCtx.currentTime - this.startAt;
  }

  private update(ticker: Ticker): void {
    const dt = Math.min(0.05, ticker.deltaMS / 1000);
    const t = Math.max(0, this.songTime);
    const { width, height } = this.app.screen;

    // Detect renderer resizes here instead of via the window event: Pixi's
    // ResizePlugin applies the new size on its own frame, so an event
    // listener can read stale dimensions (and mobile URL-bar show/hide
    // fires resize storms that would rebuild the star field needlessly).
    if (width !== this.lastW || height !== this.lastH) this.resize();

    if (!this.ended && t >= this.data.duration - 0.05) {
      this.finish();
      return;
    }

    // --- Camera & player ---
    const playerWorldX = this.geometry.xAt(t);
    const targetY = this.geometry.laneYAt(t, this.lane);
    this.playerY += (targetY - this.playerY) * Math.min(1, dt * 14);
    if (!this.camYInit) {
      this.playerY = targetY;
      this.camYInit = true;
    }
    const spineY = this.geometry.spineYAtFrame(this.geometry.frameAt(t));
    const desiredCamY = spineY - height * 0.52;
    this.camY += (desiredCamY - this.camY) * Math.min(1, dt * 3);
    const camX = playerWorldX - width * PLAYER_SCREEN_X;
    this.world.position.set(-camX, -this.camY);

    this.player.position.set(playerWorldX, this.playerY - 16);
    const slope = this.geometry.slopeAt(t);
    this.player.rotation += (slope - this.player.rotation) * Math.min(1, dt * 10);

    // --- Track lines ---
    this.drawTrack(camX, width);

    // --- Notes: spawn/cull sprites, resolve collection ---
    this.updateNotes(t, camX, width);

    // --- FX ---
    for (let i = this.fx.length - 1; i >= 0; i--) {
      const fx = this.fx[i]!;
      fx.life -= dt / 0.35;
      fx.g.scale.set(1 + (1 - fx.life) * 1.6);
      fx.g.alpha = Math.max(0, fx.life);
      if (fx.life <= 0) {
        fx.g.destroy();
        this.fx.splice(i, 1);
      }
    }

    // --- Ambience by section ---
    const sec = this.currentSection(t);
    this.bg.setIntensity(sec.intensity);
    this.bg.update(dt);

    // --- Circle colors follow the ambient palette in unison ---
    const palette = this.bg.palette;
    for (const [i, sprite] of this.noteSprites) {
      const note = this.data.notes[i]!;
      if (note.kind !== "gray") {
        sprite.tint = note.slot === "accent" ? palette.noteAccent : palette.notePrimary;
      }
    }

    // --- Grid expiry + HUD ---
    if (this.grid.update(t)) {
      /* re-render below anyway */
    }
    this.hud.renderGrid(this.grid, t, palette);
    this.hud.setProgress(t / this.data.duration);
    this.hud.update(dt);
  }

  private currentSection(t: number): Section {
    const sections = this.data.sections;
    while (this.sectionIdx < sections.length - 1 && t >= sections[this.sectionIdx]!.t1) this.sectionIdx++;
    return sections[this.sectionIdx]!;
  }

  private drawTrack(camX: number, width: number): void {
    const g = this.trackG;
    g.clear();
    const f0 = Math.max(0, this.geometry.frameForX(camX - 60) - 1);
    const f1 = Math.min(this.geometry.numFrames - 1, this.geometry.frameForX(camX + width + 60) + 1);
    const span = f1 - f0;
    const stride = Math.max(1, Math.floor(span / 160));
    for (let lane = 0 as Lane; lane <= 2; lane++) {
      let first = true;
      for (let f = f0; f <= f1; f += stride) {
        const x = this.geometry.xs[f]!;
        const y = this.geometry.laneYAtFrame(f, lane);
        if (first) {
          g.moveTo(x, y);
          first = false;
        } else {
          g.lineTo(x, y);
        }
      }
      g.stroke({ width: 7, color: this.bg.palette.lane, alpha: 0.12, cap: "round", join: "round" });
      let first2 = true;
      for (let f = f0; f <= f1; f += stride) {
        const x = this.geometry.xs[f]!;
        const y = this.geometry.laneYAtFrame(f, lane);
        if (first2) {
          g.moveTo(x, y);
          first2 = false;
        } else {
          g.lineTo(x, y);
        }
      }
      g.stroke({ width: 2.5, color: this.bg.palette.lane, alpha: 0.95, cap: "round", join: "round" });
    }
  }

  private updateNotes(t: number, camX: number, width: number): void {
    const notes = this.data.notes;
    const xMin = camX - 80;
    const xMax = camX + width + 120;

    // Advance scan start past notes that scrolled off screen long ago.
    while (
      this.noteScanFrom < notes.length &&
      this.geometry.xAt(notes[this.noteScanFrom]!.time) < xMin &&
      notes[this.noteScanFrom]!.time < t - 1
    ) {
      // A note can still be Pending here if the tab was hidden while it
      // scrolled past (rAF pauses, audio keeps playing) — settle it as
      // missed so stats stay truthful.
      if (this.noteStates[this.noteScanFrom] === NoteState.Pending) {
        this.noteStates[this.noteScanFrom] = NoteState.Missed;
        if (notes[this.noteScanFrom]!.kind === "color") this.stats.missed++;
      }
      this.releaseSprite(this.noteScanFrom);
      this.noteScanFrom++;
    }

    for (let i = this.noteScanFrom; i < notes.length; i++) {
      const note = notes[i]!;
      const nx = this.geometry.xAt(note.time);
      if (nx > xMax) break;

      // Resolve collection state.
      if (this.noteStates[i] === NoteState.Pending) {
        if (Math.abs(note.time - t) <= COLLECT_WINDOW && note.lane === this.lane) {
          this.collectNote(i, note, t, nx);
        } else if (t > note.time + COLLECT_WINDOW) {
          this.noteStates[i] = NoteState.Missed;
          if (note.kind === "color") this.stats.missed++;
          const sprite = this.noteSprites.get(i);
          if (sprite) sprite.alpha = 0.3;
        }
      }

      // Spawn sprite if needed. Drawn white and tinted live from the palette.
      if (!this.noteSprites.has(i) && this.noteStates[i] !== NoteState.Collected) {
        const sprite = new Graphics();
        const r = NOTE_RADIUS * (0.85 + 0.45 * note.strength);
        sprite.circle(0, 0, r).fill({ color: 0xffffff });
        sprite.circle(0, 0, r).stroke({ width: 1.5, color: 0x000000, alpha: 0.18 });
        sprite.tint = note.kind === "gray" ? GRAY_COLOR : 0xffffff;
        sprite.position.set(nx, this.geometry.laneYAt(note.time, note.lane));
        if (this.noteStates[i] === NoteState.Missed) sprite.alpha = 0.3;
        this.notesLayer.addChild(sprite);
        this.noteSprites.set(i, sprite);
      }
    }
  }

  private collectNote(i: number, note: NoteEvent, t: number, nx: number): void {
    this.noteStates[i] = NoteState.Collected;
    if (note.kind === "color") this.stats.collected++;
    const result = this.grid.collect(note, t);
    this.stats.score += result.points;
    if (result.combo) this.stats.combos++;
    if (result.fullRow !== null) this.stats.fullRows++;
    this.hud.setScore(this.stats.score);

    const sprite = this.noteSprites.get(i);
    const y = this.geometry.laneYAt(note.time, note.lane);
    if (sprite) {
      this.noteSprites.delete(i);
      this.fx.push({ g: sprite, life: 1 });
    }

    // Feedback floats at the player, in screen coords.
    const palette = this.bg.palette;
    const noteColor = note.slot === "accent" ? palette.noteAccent : palette.notePrimary;
    const sx = this.app.screen.width * PLAYER_SCREEN_X;
    const sy = y - this.camY - 40;
    if (result.fullRow !== null) this.hud.spawnFloat("ROW! +400", sx, sy, 0xffe066);
    else if (result.combo) this.hud.spawnFloat("COMBO +150", sx, sy, noteColor);
    else if (note.kind === "gray") this.hud.spawnFloat("BLOCKED 20s", sx, sy, 0x9aa0a8);
    void nx;
  }

  private releaseSprite(i: number): void {
    const sprite = this.noteSprites.get(i);
    if (sprite) {
      sprite.destroy();
      this.noteSprites.delete(i);
    }
  }

  private buildPlayer(): void {
    const g = new Graphics();
    g.roundRect(-16, -16, 32, 32, 5).fill({ color: 0xe8842c });
    g.roundRect(-16, -16, 32, 32, 5).stroke({ width: 2, color: 0xffb066, alpha: 0.9 });
    this.player.addChild(g);
  }

  private finish(): void {
    this.ended = true;
    this.app.ticker.remove(this.update);
    this.onEnd({ ...this.stats });
  }
}
